import { useMemo } from 'react';
import { css } from '@emotion/react';
import { AppointmentContactType } from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { CheckListFilterSelector, CheckListPopoverFilterSelector, FilterTraySection } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { trackingIds } from '../../tracking-ids';
import { PatientTypeChip } from '../atoms/appointment-status-chips';

interface AppointmentContactTypeFilterProps {
  value?: AppointmentContactType;
  onChange: (contactType: AppointmentContactType | undefined) => void;
  isTrayFilter?: boolean;
}

const normalizeAppointmentContactType = (types: AppointmentContactType[]) =>
  types.length === 0 ? undefined : types.length === 1 ? types[0] : AppointmentContactType.APPOINTMENT_ALL_PATIENTS;

const denormalizeAppointmentContactType = (val?: AppointmentContactType) =>
  val === AppointmentContactType.APPOINTMENT_ALL_PATIENTS
    ? [
        AppointmentContactType.APPOINTMENT_EXISTING_PATIENTS,
        AppointmentContactType.APPOINTMENT_NEW_PATIENTS,
        AppointmentContactType.APPOINTMENT_UNSHEDULED_PATIENTS,
      ]
    : val
    ? [val]
    : [];

export const AppointmentContactTypeFilter = ({ value, onChange, isTrayFilter }: AppointmentContactTypeFilterProps) => {
  const { t } = useTranslation('analytics');

  const contactTypeOptions = useMemo(() => {
    return {
      [AppointmentContactType.APPOINTMENT_EXISTING_PATIENTS]: (
        <PatientTypeChip css={styles.chipStyles} type={AppointmentContactType.APPOINTMENT_EXISTING_PATIENTS} />
      ),
      [AppointmentContactType.APPOINTMENT_NEW_PATIENTS]: (
        <PatientTypeChip
          type={AppointmentContactType.APPOINTMENT_NEW_PATIENTS}
          css={css`
            max-width: none;
          `}
        />
      ),
    };
  }, []);

  if (isTrayFilter) {
    return (
      <FilterTraySection label={t('Contact Type')} onReset={() => onChange(undefined)} disableReset={!value}>
        <CheckListFilterSelector
          options={contactTypeOptions}
          label={t('Contact Type')}
          onChange={(type) => {
            onChange(normalizeAppointmentContactType(type as AppointmentContactType[]));
          }}
          value={denormalizeAppointmentContactType(value)}
          showLabel={false}
          trackingIdBase={`${trackingIds.filterTrayToggle}-contact-type`}
        />
      </FilterTraySection>
    );
  }

  return (
    <CheckListPopoverFilterSelector
      options={contactTypeOptions}
      label={t('Contact Type')}
      defaultValue={[]}
      onChange={(type) => {
        onChange(normalizeAppointmentContactType(type as AppointmentContactType[]));
      }}
      renderIcon={(isFilterActive) => <Icon name='contacts-small' color={isFilterActive ? 'white' : 'default'} />}
      value={denormalizeAppointmentContactType(value)}
      trackingIdBase={`${trackingIds.filtersOpen}-contact-type`}
      disableClearWhenEmpty
    />
  );
};

const styles = {
  chipStyles: css`
    max-width: none;
  `,
};
